// src/routes/AppRoutes.jsx
import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { useAuthContext } from "../contexts/AuthContext";

// Layouts
import PublicLayout from "../components/layout/PublicLayout";
import DashboardLayout from "../components/layout/DashboardLayout";

// Admin
import AdminApp from "../admin/AdminApp";

// Public (B2C)
import Home from "../pages/public/Home";
import PublicEvents from "../pages/public/Events";
import EventDetails from "../pages/public/EventDetails";
import Checkout from "../pages/public/Checkout";
import CheckoutSuccess from "../pages/public/CheckoutSuccess";
import MyTickets from "../pages/public/MyTickets";
import Login from "../pages/public/Login";
import Organizers from "../pages/public/Organizers";
import OrganizerSignup from "../pages/public/OrganizerSignup";
import OrganizerRequestSent from "../pages/public/OrganizerRequestSent";
import HowItWorks from "../pages/public/HowItWorks";
import Fees from "../pages/public/Fees";
import Privacy from "../pages/public/Privacy";
import Terms from "../pages/public/Terms";
import Contact from "../pages/public/Contact";
import Perfil from "../pages/public/MyProfile";

// Dashboard (B2B)
import DashboardHome from "../pages/dashboard/DashboardHome";
import Events from "../pages/dashboard/Events";
import EventEditor from "../pages/dashboard/EventEditor";
import LotsManager from "../pages/dashboard/LotsManager";
import Sales from "../pages/dashboard/Sales";
import Reports from "../pages/dashboard/Reports";
import Settings from "../pages/dashboard/Settings";
import NovoEventoPage from "../pages/dashboard/novoEvento/NovoEventoPage";
import CheckinManager from "../pages/dashboard/CheckinManager";

// Check-in (público, sem login)
import CheckinHome from "../pages/checkin/CheckinHome";
import Scanner from "../pages/checkin/Scanner";

// ─── GUARDS ────────────────────────────────────────────────────────────────

/**
 * Rota que exige customer autenticado (role === "customer").
 * Organizers aprovados também têm acesso (são clientes também).
 * Se não logado → /login. Se organizer pending/rejected → bloqueado.
 */
function CustomerRoute({ children }) {
  const { isAuthenticated, loading } = useAuthContext();
  const location = useLocation();

  if (loading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * Rota do dashboard: exige role === "organizer" E approvalStatus === "approved".
 * pending → /organizadores/solicitacao-enviada
 * rejected → /organizadores/solicitacao-rejeitada (ou /organizadores/login com estado)
 * não logado → /login
 */
function DashboardRoute({ children }) {
  const { isAuthenticated, role, approvalStatus, loading } = useAuthContext();
  const location = useLocation();

  if (loading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (role !== "organizer") {
    return <Navigate to="/" replace />;
  }

  if (approvalStatus === "pending") {
    return <Navigate to="/organizadores/solicitacao-enviada" replace />;
  }

  if (approvalStatus === "rejected") {
    return (
      <Navigate to="/organizadores/login" state={{ rejected: true }} replace />
    );
  }

  // approved
  return children;
}

// ─── ROUTER ────────────────────────────────────────────────────────────────

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* ========================= PUBLIC ========================= */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/eventos" element={<PublicEvents />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/eventos/:eventId" element={<EventDetails />} />
          <Route path="/checkout/sucesso" element={<CheckoutSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/organizadores" element={<Organizers />} />
          <Route path="/organizadores/login" element={<OrganizerSignup />} />
          <Route
            path="/organizadores/solicitacao-enviada"
            element={<OrganizerRequestSent />}
          />
          <Route path="/como-funciona" element={<HowItWorks />} />
          <Route path="/taxas" element={<Fees />} />
          <Route path="/privacidade" element={<Privacy />} />
          <Route path="/termos" element={<Terms />} />
          <Route path="/contato" element={<Contact />} />

          {/* Rotas que exigem customer autenticado */}
          <Route
            path="/meus-ingressos"
            element={
              <CustomerRoute>
                <MyTickets />
              </CustomerRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <CustomerRoute>
                <Perfil />
              </CustomerRoute>
            }
          />
        </Route>

        {/* ========================= ADMIN ========================= */}
        {/* AdminApp gerencia internamente a rota /admin/login e o guard */}
        <Route path="/admin/*" element={<AdminApp />} />

        {/* ========================= DASHBOARD ========================= */}
        <Route
          path="/dashboard"
          element={
            <DashboardRoute>
              <DashboardLayout />
            </DashboardRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="eventos" element={<Events />} />
          <Route path="eventos/novo" element={<NovoEventoPage />} />
          <Route path="eventos/:eventId" element={<EventEditor />} />
          <Route path="lotes/:eventId" element={<LotsManager />} />
          <Route path="vendas" element={<Sales />} />
          <Route path="relatorios" element={<Reports />} />
          <Route path="configuracoes" element={<Settings />} />
          <Route path="checkin" element={<CheckinManager />} />
        </Route>

        {/* ========================= CHECK-IN (público) ========================= */}
        <Route path="/checkin">
          <Route index element={<CheckinHome />} />
          <Route path="scanner/:sessionCode" element={<Scanner />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
